// =============================================================================
// enemies.ts — Bestiário nacional: inimigos comuns e bosses baseados em
// acontecimentos reais. Todo mundo leva porrada: esquerda, direita e Centrão.
// A IA de cada boss vive em src/server/domain/bosses; aqui ficam os dados.
// =============================================================================

export interface RangedSpec {
  cooldown: number;
  speed: number;
  damage: number;
  range: number;
}

export interface EnemyDef {
  id: string;
  nome: string;
  emoji: string;
  cor: number;
  hp: number;
  speed: number;
  damage: number;
  xp: number;             // votos dropados
  pontos: number;         // pontos no ranking por abate (issue #88) — calibrado
                          // pela dificuldade de matar (HP, velocidade, alcance)
  radius: number;
  quotes: string[];
  ranged?: RangedSpec;    // atira de longe
  charger?: boolean;      // trava a mira e acelera
  ghost?: boolean;        // translúcido (aliado fantasma)
}

export interface BossDef {
  id: string;
  nome: string;
  emoji: string;
  cor: number;
  hp: number;
  speed: number;
  damage: number;
  xp: number;
  pontos: number;         // pontos no ranking por derrubar o boss (issue #88)
  radius: number;
  ai: string;             // chave do comportamento (src/server/domain/bosses)
  intro: string;
  quotes: string[];
}

// Inimigos comuns — bestiário completo (recontratado pela issue #84, com
// sprites novos). A urna possuída segue como mob base a partida inteira; o
// resto entra progressivamente conforme o WAVE_TABLE lá embaixo.
export const ENEMIES = {
  urna_possuida: {
    id: 'urna_possuida', nome: 'Urna Possuída', emoji: '🗳️', cor: 0xb8e0a0,
    hp: 26, speed: 130, damage: 6, xp: 1, pontos: 10, radius: 16,
    quotes: [
      'FIM. DE. VOTAÇÃO.', 'CONFIRMA... sua eliminação', 'Voto computado. Pra sempre.',
      'BEEP. BEEP. BEEP.', 'CANDIDATO NÃO ENCONTRADO.', 'SEU TÍTULO FOI CANCELADO.',
      'VOTA. OU. MORRE.', 'ERRO 171: DEMOCRACIA NÃO RESPONDE.',
    ],
  },
  santinho_voador: {
    id: 'santinho_voador', nome: 'Santinho Voador', emoji: '📄', cor: 0xf2e9c8,
    hp: 11, speed: 175, damage: 4, xp: 1, pontos: 10, radius: 12,
    quotes: [
      'VOTE 171! VOTE 171!', 'Sou ficha limpa*! (*consultar rodapé)',
      'Cola em mim que eu colo em você!', 'Reciclagem? Só de promessa.',
      'Caí do céu direto no bueiro!',
    ],
  },
  titulo_zumbi: {
    id: 'titulo_zumbi', nome: 'Título Zumbi', emoji: '🪪', cor: 0x9db8a0,
    hp: 19, speed: 145, damage: 5, xp: 1, pontos: 15, radius: 14,
    quotes: [
      'Vooootoooos...', 'Morri em 1994, voto até hoje.',
      'Justificar? Nunca ouvi falar.', 'Minha zona eleitoral é o ALÉM.',
      'Cérebro não, SEÇÃO ELEITORAL!',
    ],
  },
  biometria_fantasma: {
    id: 'biometria_fantasma', nome: 'Biometria Fantasma', emoji: '🖐️', cor: 0xa8c8e8,
    hp: 30, speed: 120, damage: 8, xp: 2, pontos: 25, radius: 15, ghost: true,
    quotes: [
      'Digital não reconhecida... PRA SEMPRE.', 'Encoste o dedo... no caixão.',
      'Sua identidade agora é MINHA.', 'Tente novamente. Tente. Novamente.',
    ],
  },
  robo_fake_news: {
    id: 'robo_fake_news', nome: 'Robô de Fake News', emoji: '🤖', cor: 0x5dade2,
    hp: 38, speed: 90, damage: 6, xp: 2, pontos: 35, radius: 16,
    ranged: { cooldown: 2.4, speed: 180, damage: 6, range: 320 },
    quotes: [
      'COMPARTILHE ANTES QUE APAGUEM!', 'Fonte: confia.',
      'Recebi no zap da família.', 'ISSO A MÍDIA NÃO MOSTRA!',
      'Checagem? Bloqueado.',
    ],
  },
  carro_de_som: {
    id: 'carro_de_som', nome: 'Carro de Som', emoji: '📢', cor: 0xf39c12,
    hp: 66, speed: 115, damage: 12, xp: 3, pontos: 45, radius: 20, charger: true,
    quotes: [
      'TESTANDO, SOM, TESTANDO!', 'É O ÚNICO QUE PODE TE SALVAR!',
      'DEZ, VINTE, TRINTA DECIBÉIS!', 'Aumenta que é jingle!',
      'SILÊNCIO É PRA PERDEDOR!',
    ],
  },
  mala_de_dinheiro: {
    id: 'mala_de_dinheiro', nome: 'Mala de Dinheiro', emoji: '💼', cor: 0x784212,
    hp: 113, speed: 70, damage: 10, xp: 4, pontos: 55, radius: 20,
    quotes: [
      'Sou só uma mala de livros!', 'Caixa 2? Isso é caixa 3.',
      'R$ 51 mil na cueca era troco.', 'Doação NÃO declarada de campanha.',
      'Não olha dentro. Confia.',
    ],
  },
  pesquisa_maldita: {
    id: 'pesquisa_maldita', nome: 'Pesquisa Maldita', emoji: '📊', cor: 0xaf7ac5,
    hp: 56, speed: 85, damage: 8, xp: 3, pontos: 55, radius: 17,
    ranged: { cooldown: 2.6, speed: 200, damage: 8, range: 340 },
    quotes: [
      'Margem de erro: SUA VIDA.', 'Você tem 2 pontos de chance.',
      'Empate técnico com o caixão.', 'Amostra: 3 pessoas do meu prédio.',
      'Institutos divergem, você morre.',
    ],
  },
  drone_fiscal: {
    id: 'drone_fiscal', nome: 'Drone Fiscalizador', emoji: '🛸', cor: 0x85c1e9,
    hp: 47, speed: 190, damage: 11, xp: 3, pontos: 65, radius: 14,
    quotes: [
      'Boca de urna DETECTADA.', 'Sorria, você está sendo multado.',
      'Voo rasante da fiscalização!', 'Denúncia anônima: VOCÊ.',
    ],
  },
  trio_eletrico: {
    id: 'trio_eletrico', nome: 'Trio Elétrico Assombrado', emoji: '🚚', cor: 0xe74c3c,
    hp: 244, speed: 95, damage: 16, xp: 6, pontos: 120, radius: 26, charger: true,
    quotes: [
      'ATRÁS DO TRIO SÓ NÃO VAI QUEM MORREU!', 'Comício encerrado. Você também.',
      'Cachê do artista? Emenda parlamentar.', 'GRAVE NO SEU CORAÇÃO: 99!',
      'O paredão agora é literal.',
    ],
  },
} satisfies Record<string, EnemyDef>;

export type EnemyTypeId = keyof typeof ENEMIES;

// =============================================================================
// BOSSES — um a cada ~2min30, baseados em fatos que o Brasil preferia esquecer.
// =============================================================================
// NB: a `id`/`ai`/retrato (fx boss_<id>) são internos e ficam FIXOS — o retrato
// desenhado (references/bosses.png) é quem manda no nome. Por isso alguns ids
// não “combinam” com o nome (ex.: id `mensalao2` mostra o Milei): o que o
// jogador vê (nome/intro/quotes) casa com a ARTE, não com o id legado.
export const BOSSES = {
  // Retrato: mulher loira de terninho roxo com microfone de TV = repórter
  live_19h: {
    id: 'live_19h', nome: 'Repórter da Globo', emoji: '📺', cor: 0xf7d117,
    hp: 27000, speed: 55, damage: 14, xp: 40, pontos: 500, radius: 46, ai: 'live_19h',
    intro: 'BOSS: A REPÓRTER DA GLOBO — ao vivo do caos: ela CONVOCA a audiência em ondas e enquadra quem chega perto no microfonaço. Fontes ouvidas pela reportagem...',
    quotes: ['Fontes ouvidas pela reportagem confirmam.', 'Procurada, a horda não respondeu até o fechamento desta matéria.', 'Reveja agora no G1!', 'Isso aqui é jornalismo, não militância — juro.'],
  },
  // Retrato: homem de cabelo selvagem, MOTOSSERRA e faixa argentina = Milei
  mensalao2: {
    id: 'mensalao2', nome: 'Javier Milei', emoji: '🪚', cor: 0x8e44ad,
    hp: 55000, speed: 70, damage: 16, xp: 50, pontos: 600, radius: 44, ai: 'mensalao2',
    intro: 'BOSS: JAVIER MILEI — motosserra no orçamento: ele CORTA os votos da sua chapa (seu XP) e vira vida com o corte. ¡No hay plata!',
    quotes: ['Funcionário público? ¡VAGABUNDO!', '¡No hay plata!', '¡AFUERA!', 'El Estado é uma organização criminosa. ¡Viva la libertad, carajo!'],
  },
  // Retrato: mulher de faixa presidencial verde-amarela = Michelle
  impeachment_dilma: {
    id: 'impeachment_dilma', nome: 'Michelle Bolsonaro', emoji: '🙏', cor: 0xc0392b,
    hp: 65000, speed: 60, damage: 15, xp: 55, pontos: 700, radius: 48, ai: 'impeachment_dilma',
    intro: 'BOSS: MICHELLE BOLSONARO — culto com panelaço: cada jogador ganha uma panela na cabeça enquanto o exército de fiéis ora. Glória!',
    quotes: ['Glória a Deus, ó glória!', 'Vocês são o meu exército de Gideão!', 'Interpretei em Libras e você não entendeu mesmo assim.', 'O verdadeiro poder usa saia e ora de joelhos.'],
  },
  // Retrato: senhora de óculos e vestido vermelho/dourado = Janja
  cpi_covid: {
    id: 'cpi_covid', nome: 'Janja da Silva', emoji: '👑', cor: 0xe67e22,
    hp: 75000, speed: 50, damage: 15, xp: 60, pontos: 800, radius: 50, ai: 'cpi_covid',
    intro: 'BOSS: JANJA — a primeira-dama subiu ao palco: holofotes QUEIMAM e ela convoca fãs de todo canto. Sai da luz.',
    quotes: ['Primeira-dama também opina, viu?', 'Quem organizou a posse fui EU.', 'Isso aqui tem o meu dedo, sim senhor.', 'Me elegeram junto — dois pelo preço de um.'],
  },
  // Retrato: homem de óculos escuros e distintivo com a balança = Japonês da Federal
  stf_bolso: {
    id: 'stf_bolso', nome: 'Japonês da Federal', emoji: '🕶️', cor: 0x2c3e50,
    hp: 85000, speed: 45, damage: 16, xp: 65, pontos: 900, radius: 50, ai: 'stf_bolso',
    intro: 'BOSS: O JAPONÊS DA FEDERAL — de óculos escuros e distintivo: ele PARALISA a arena no grito de "mandado" e distribui martelada. Mão na cabeça.',
    quotes: ['Documento! Todo mundo pra averiguação.', 'Óculos escuro é pra ninguém ver quem eu miro.', 'Sou os olhos do Mito no meio do povo.', 'Aqui quem dá a ordem de prisão sou eu.'],
  },
  // Retrato: caça-níquel/cassino cuspindo dinheiro = a própria máquina
  orcamento_secreto: {
    id: 'orcamento_secreto', nome: 'Caça-Níquel do Orçamento', emoji: '🎰', cor: 0x7f8c8d,
    hp: 95000, speed: 55, damage: 17, xp: 70, pontos: 1000, radius: 52, ai: 'orcamento_secreto',
    intro: 'BOSS: O CAÇA-NÍQUEL DO ORÇAMENTO — a máquina paga propina sem nota fiscal: aliados fantasmas caem sem parar e a verba pinga de volta. RP-9 na veia.',
    quotes: ['RP-9 aprovada, relator? SIGILO.', 'A casa sempre ganha — e a casa é o Centrão.', 'Emenda pix caindo na conta...', 'Trator superfaturado a caminho!'],
  },
  // Retrato: careca de barba e óculos apontando o dedo com um livro = Enéas
  lula_planalto: {
    id: 'lula_planalto', nome: 'Enéas Carneiro', emoji: '☢️', cor: 0xd42a2a,
    hp: 105000, speed: 60, damage: 18, xp: 80, pontos: 1200, radius: 52, ai: 'lula_planalto',
    intro: 'BOSS: ENÉAS CARNEIRO — em 30 segundos ele reergue a horda inteira no grito e fecha com o bordão. MEU NOME É ENÉAS!',
    quotes: ['MEU NOME É ENÉAS!', 'O Brasil tem urânio pra fazer quinhentas bombas!', 'Quem quiser que me acompanhe; os covardes ficarão para trás!', 'Trinta segundos de TV não me bastam!'],
  },
  // Retrato: homem loiro de gravata vermelha e punhos cerrados = Trump
  oito_jan_invasao: {
    id: 'oito_jan_invasao', nome: 'Donald Trump', emoji: '🦅', cor: 0x27ae60,
    hp: 120000, speed: 75, damage: 20, xp: 100, pontos: 1500, radius: 54, ai: 'oito_jan_invasao',
    intro: 'BOSS FINAL: DONALD TRUMP — "stop the count!": ele cerca cada jogador com a manada e, na metade da vida, manda QUEBRAR TUDO. Sobreviva.',
    quotes: ['Make the Horde Great Again!', 'This election was RIGGED — everybody knows it!', 'Nobody survives better than me, believe me!', 'We fight like hell, or you won\'t have a country anymore!'],
  },
} satisfies Record<string, BossDef>;

export type BossId = keyof typeof BOSSES;

// Lookups por id vindo da rede/snapshot (string qualquer)
export const ENEMY_TABLE: Record<string, EnemyDef | undefined> = ENEMIES;
export const BOSS_TABLE: Record<string, BossDef | undefined> = BOSSES;

// Cronograma dos bosses (segundos de partida -> boss)
export interface BossScheduleEntry {
  at: number;
  boss: BossId;
}
export const BOSS_SCHEDULE: BossScheduleEntry[] = [
  { at: 150, boss: 'live_19h' },
  { at: 300, boss: 'mensalao2' },
  { at: 450, boss: 'impeachment_dilma' },
  { at: 600, boss: 'cpi_covid' },
  { at: 750, boss: 'stf_bolso' },
  { at: 900, boss: 'orcamento_secreto' },
  { at: 1050, boss: 'lula_planalto' },
  { at: 1140, boss: 'oito_jan_invasao' },
];

// Ondas: composição por faixa de tempo (o servidor sorteia dessa lista)
export interface WaveRow {
  until: number;
  pool: EnemyTypeId[];
}
// Duplicatas no pool funcionam como peso; cada faixa lista TUDO que pode
// spawnar naquela janela (as faixas são janelas, não acumulam).
export const WAVE_TABLE: WaveRow[] = [
  // 0:00–2:30 — só urna: o eleitor aprende a apanhar do básico
  { until: 150, pool: ['urna_possuida'] },
  // 2:30–5:00 — chega a papelada: santinhos e títulos zumbis
  { until: 300, pool: [
    'urna_possuida', 'urna_possuida', 'urna_possuida', 'urna_possuida',
    'santinho_voador', 'santinho_voador', 'santinho_voador',
    'titulo_zumbi', 'titulo_zumbi',
  ] },
  // 5:00–7:00 — biometria fantasma assombra a fila
  { until: 420, pool: [
    'urna_possuida', 'urna_possuida', 'urna_possuida',
    'santinho_voador', 'santinho_voador',
    'titulo_zumbi', 'titulo_zumbi',
    'biometria_fantasma', 'biometria_fantasma',
  ] },
  // 7:00–9:00 — robôs de fake news abrem fogo de longe
  { until: 540, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador', 'santinho_voador',
    'titulo_zumbi', 'titulo_zumbi',
    'biometria_fantasma', 'biometria_fantasma',
    'robo_fake_news', 'robo_fake_news',
  ] },
  // 9:00–11:00 — carro de som entra cantando pneu (e jingle)
  { until: 660, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador',
    'titulo_zumbi', 'titulo_zumbi',
    'biometria_fantasma', 'biometria_fantasma',
    'robo_fake_news', 'robo_fake_news',
    'carro_de_som', 'carro_de_som',
  ] },
  // 11:00–13:00 — malas de dinheiro desfilam sem nota fiscal
  { until: 780, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador', 'titulo_zumbi',
    'biometria_fantasma', 'biometria_fantasma',
    'robo_fake_news', 'robo_fake_news',
    'carro_de_som', 'carro_de_som',
    'mala_de_dinheiro', 'mala_de_dinheiro',
  ] },
  // 13:00–15:00 — pesquisas malditas divulgam sua margem de erro
  { until: 900, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador', 'titulo_zumbi', 'biometria_fantasma',
    'robo_fake_news', 'robo_fake_news',
    'carro_de_som', 'carro_de_som',
    'mala_de_dinheiro', 'mala_de_dinheiro',
    'pesquisa_maldita', 'pesquisa_maldita',
  ] },
  // 15:00–16:00 — drones fiscalizadores em voo rasante
  { until: 960, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador', 'titulo_zumbi', 'biometria_fantasma',
    'robo_fake_news', 'robo_fake_news',
    'carro_de_som',
    'mala_de_dinheiro', 'mala_de_dinheiro',
    'pesquisa_maldita', 'pesquisa_maldita',
    'drone_fiscal', 'drone_fiscal',
  ] },
  // 16:00+ — apoteose: trio elétrico assombrado fecha o carnaval eleitoral
  // (vale indefinidamente — cobre também um futuro modo infinito)
  { until: 9999, pool: [
    'urna_possuida', 'urna_possuida',
    'santinho_voador', 'titulo_zumbi', 'biometria_fantasma',
    'robo_fake_news', 'carro_de_som',
    'mala_de_dinheiro', 'mala_de_dinheiro',
    'pesquisa_maldita',
    'drone_fiscal', 'drone_fiscal',
    'trio_eletrico',
  ] },
];

// Bordões genéricos soltados por inimigos aleatórios (limitado por tick p/ não virar spam)
export const BORDOES_GERAIS: string[] = [
  'Lula ladrão!', 'Bolsonaro genocida!', 'Vai pra Cuba!', 'Vai pra Miami!',
  'Ditadura do Judiciário!', 'Imagina na Copa!', 'Tá tudo dominado!',
  'MITO!', 'GOLPE!', 'Paz e amor!', 'Vai tomar no...', 'É ELE! É ELE!',
  'Fora TODOS!', 'Meu partido é o Brasil!', 'Sem anistia!', 'Anistia já!',
  'O Brasil virou uma Venezuela!', 'Primeiro eles pegam o PIX...',
  'Acorda Brasil!', 'Foi o posto Ipiranga!', 'Compra no Paraguai!',
];

// Índice numérico de tipos (para snapshots compactos: envia índice, não string)
export const ETYPE_IDS: string[] = [...Object.keys(ENEMIES), ...Object.keys(BOSSES)];
export const ETYPE_INDEX: Record<string, number> = Object.fromEntries(ETYPE_IDS.map((id, i) => [id, i]));
export function typeDef(id: string): EnemyDef | BossDef | undefined {
  return ENEMY_TABLE[id] ?? BOSS_TABLE[id];
}

// Frases de fim de jogo
export const FRASES_DERROTA: string[] = [
  'Mais um dia normal no Brasil.',
  'Game over: o Centrão venceu. Como sempre.',
  'Você caiu... mas a CPMF voltou. Duas tragédias.',
];
export const FRASES_VITORIA: string[] = [
  'Parabéns, você sobreviveu… até a próxima eleição.',
  'Você venceu! Prêmio: 4 anos de paz... piada, né? Tem eleição municipal ano que vem.',
];
